import type {
  DashboardStatsTypes
} from '@flashbacktech/flashbackclient/api';

export type DatePreset = '24h' | '7d' | '30d' | '90d' | '180d' | '365d';

export interface V2ScopeParams {
  scope: DashboardStatsTypes.DashboardScope;
  id?: string;
}

export function getV2Scope(explorerWs: string | null, explorerRepo: string | null): V2ScopeParams {
  if (explorerRepo) return { scope: 'repo', id: explorerRepo };
  if (explorerWs) return { scope: 'workspace', id: explorerWs };
  return { scope: 'org' };
}

const BUCKET_SIZE_MAP: Record<DatePreset, DashboardStatsTypes.DashboardTimeBucketSize> = {
  '24h': '10m',
  '7d': '1h',
  '30d': '6h',
  '90d': '12h',
  '180d': '1d',
  '365d': '1d',
};

export function getTimeBucketSize(preset: DatePreset): DashboardStatsTypes.DashboardTimeBucketSize {
  return BUCKET_SIZE_MAP[preset];
}

export function buildTimeSeriesParams(
  explorerWs: string | null,
  explorerRepo: string | null,
  dateRange: DatePreset,
  extra?: Record<string, string | undefined>,
): DashboardStatsTypes.DashboardStatsQueryParams & { timeBucketSize?: DashboardStatsTypes.DashboardTimeBucketSize } {
  const { scope, id } = getV2Scope(explorerWs, explorerRepo);
  return {
    scope,
    ...(id ? { id } : {}),
    window: dateRange as DashboardStatsTypes.DashboardWindow,
    ...extra,
  };
}

export function buildBreakdownParams(
  explorerWs: string | null,
  explorerRepo: string | null,
  dateRange: DatePreset,
  extra?: Partial<DashboardStatsTypes.DashboardBreakdownQueryParams>,
): DashboardStatsTypes.DashboardBreakdownQueryParams {
  const { scope, id } = getV2Scope(explorerWs, explorerRepo);
  return {
    scope,
    ...(id ? { id } : {}),
    window: dateRange as DashboardStatsTypes.DashboardWindow,
    ...extra,
  };
}

export function formatBucketLabel(tsSeconds: number, preset: DatePreset): string {
  const d = new Date(tsSeconds * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  switch (preset) {
    case '24h':
      return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    case '7d':
      return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${pad(d.getHours())}h`;
    case '30d':
    case '90d':
    case '180d':
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    case '365d':
      return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    default:
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
}

/**
 * Transform a V2 time-series array (with `ts` field in unix seconds)
 * into chart-ready data with a formatted `name` label.
 */
export function formatV2Series<T extends { ts: number }>(
  series: T[],
  preset: DatePreset,
): Array<Omit<T, 'ts'> & { name: string; ts: number }> {
  return series.map((point) => ({
    ...point,
    name: formatBucketLabel(point.ts, preset),
  }));
}
